import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { isEqual, noop } from 'lodash';
import { nanoid } from 'nanoid';

import useForm from 'hooks/useForm';
import { getPersonFullName } from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';

import { openDialogAction, updateDialogOptionsAction } from 'store/global/actions';
import { selectLoanCicReasons } from 'store/loanApplications/selectors';

import ManageCICModal from '../..';

import {
  getLoanCicReasonFormConfig,
  LOAN_CIC_REASONS_EMPTY_MESSAGE,
  LOAN_CIC_REASONS_MODAL_HEADER,
} from './config';

export const useOpenLoanCicReasonsModal = () => {
  const dispatch = useDispatch();

  const loanCicReasons = useSelector(selectLoanCicReasons, isEqual);

  const modalId = useMemo(() => nanoid(), []);

  const { initialFormValues } = useMemo(() => {
    const applicationData = loanCicReasons.reduce((acc, reason) => {
      acc[reason.loanCicReasonId] = {
        textNote: reason.note ?? '',
        reasonStatus: reason.status ?? '',
        modifiedDate: reason.modifiedDate ?? '',
        modifiedBy: reason.modifiedBy ? getPersonFullName(reason.modifiedBy) : '',
      };

      return acc;
    }, {});

    return getConfigInfo({
      applicationData,
      config: getLoanCicReasonFormConfig(loanCicReasons),
      isNestedForm: true,
    });
  }, [loanCicReasons]);

  const formContext = useForm(initialFormValues);
  const { setForm, setInitialFormValues } = formContext;

  const renderBody = useCallback(
    () => (
      <ManageCICModal
        isReadOnly
        cicReasons={loanCicReasons}
        emptyMessage={LOAN_CIC_REASONS_EMPTY_MESSAGE}
        formContext={formContext}
        openCICModal={noop}
      />
    ),
    [loanCicReasons, formContext],
  );

  const openLoanCicReasonsModal = useCallback(() => {
    dispatch(
      openDialogAction({
        id: modalId,
        header: LOAN_CIC_REASONS_MODAL_HEADER,
        isRestyled: true,
        size: ModalSize.LARGE,
        hasScrollY: true,
        body: renderBody,
      }),
    );
  }, [dispatch, modalId, renderBody]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  useEffect(() => {
    dispatch(updateDialogOptionsAction(modalId, { body: renderBody }));
  }, [dispatch, modalId, renderBody]);

  return openLoanCicReasonsModal;
};
